import { getMonthlyBillAmount } from '@fruitful/fruitils';
import { type AccountSummary } from '@fruitful/types';
import { capitalize } from 'lodash';
import {
    BillCategoryIcons,
    CircleDashedIcon,
    HelpCircleIcon,
    type IconType,
    ReceiptTextIcon,
    tokens,
} from 'smoothie';

import {
    type _Bill,
    BillCategory,
    BillCategoryBackgroundColors,
    BillCategoryNames,
    BillingCadence,
    type IBill,
    type ManualBill,
    type PartialBill,
} from '../../bills/types/Bill';
import { getOrdinalSuffix, properCase } from '../misc';
import { convertToDate } from './dateUtils';

export const convertCategoryToIcon = (category?: string): IconType => {
    if (category === undefined) {
        return ReceiptTextIcon;
    }

    if (category === 'all') {
        return CircleDashedIcon;
    }

    return BillCategoryIcons[category as BillCategory] ?? HelpCircleIcon;
};

export const sortCategories = (a: BillCategory, b: BillCategory): number => {
    const ordering: Record<BillCategory, number> = {
        [BillCategory.RENT_MORTGAGE]: 8,
        [BillCategory.CHILDCARE]: 7,
        [BillCategory.LOANS]: 6,
        [BillCategory.CREDIT_CARD_MINIMUM]: 5,
        [BillCategory.UTILITIES]: 4,
        [BillCategory.INSURANCE]: 3,
        [BillCategory.SUBSCRIPTIONS]: 2,
        [BillCategory.OTHER]: 1,
    };

    return ordering[b] - ordering[a];
};

export const isPartialBillValid = (
    partialBill: PartialBill,
): partialBill is IBill => {
    return Object.keys(partialBill).every(
        (key) => partialBill[key as keyof IBill] !== undefined,
    );
};

export const mapPartialBillToBill = (partialBill: PartialBill): IBill => {
    if (isPartialBillValid(partialBill)) {
        return partialBill;
    } else {
        const undefinedFields = Object.keys(partialBill).filter(
            (key) => partialBill[key as keyof IBill] === undefined,
        );
        throw new Error(
            `PartialBill is not valid IBill. Undefined fields: ${undefinedFields.join(', ')}`,
        );
    }
};

/*
 * Takes the value from Date.getDay() and returns the string day of the week, or Unknown
 * if the date was invalid.
 */
export const getDayOfWeekString = (dateVal: number): string => {
    if (Number.isNaN(dateVal)) {
        return 'Unknown';
    }

    switch (dateVal) {
        case 0:
            return 'Sunday';
        case 1:
            return 'Monday';
        case 2:
            return 'Tuesday';
        case 3:
            return 'Wednesday';
        case 4:
            return 'Thursday';
        case 5:
            return 'Friday';
        case 6:
            return 'Saturday';
        default:
            return 'Unknown';
    }
};

export const getCadenceDisplayString = (
    cadence: BillingCadence | undefined,
): string => {
    return cadence !== undefined ? capitalize(cadence).replace('_', '-') : '';
};

export const getCategoryDisplayString = (
    category: string | undefined,
): string => {
    return BillCategoryNames[category as BillCategory] ?? 'Other';
};

export const getCategoryExampleString = (
    category: string | undefined,
): string => {
    switch (category) {
        case BillCategory.CHILDCARE:
            return 'pre-school, nanny services';
        case BillCategory.INSURANCE:
            return 'auto, renters, independent life';
        case BillCategory.LOANS:
            return 'car loans';
        case BillCategory.RENT_MORTGAGE:
            return 'rent or mortgage';
        case BillCategory.SUBSCRIPTIONS:
            return 'streaming services, or gym memberships';
        case BillCategory.UTILITIES:
            return 'power, water, internet, cell phone';
        default:
            return '';
    }
};

const normalizeLastPaidAt = (lastPaidAt: Date | string | null | undefined) => {
    if (lastPaidAt == null) {
        return null;
    }

    const date = convertToDate(lastPaidAt);
    return Number.isNaN(date.getTime()) ? null : date;
};

export const transformBillFromServer = (billFromServer: PartialBill): IBill => {
    const {
        name,
        payee,
        amount: amountInCents,
        cadence,
        category,
    } = billFromServer;
    const amountInDollars = (amountInCents ?? 0) / 100;

    // Normalize category to lowercase (server might send uppercase)
    const normalizedCategory = (category?.toLowerCase() ??
        BillCategory.OTHER) as BillCategory;

    const completedBill = {
        ...billFromServer,
        category: normalizedCategory,
        name: properCase(name ?? payee ?? ''),
        amount: amountInDollars,
        monthlyAmount: getMonthlyBillAmount(amountInDollars, cadence),
        timingLabel:
            billFromServer.timingLabel ?? getCadenceDisplayString(cadence),
        timingDescription:
            billFromServer.timingDescription ??
            getCadenceDisplayString(cadence),
        removedAt: billFromServer.removedAt ?? null,
        lastPaidAt: normalizeLastPaidAt(billFromServer.lastPaidAt),
        transactions: billFromServer.transactions ?? [],
        ...(billFromServer.proposedChanges !== undefined && {
            proposedChanges: billFromServer.proposedChanges,
        }),
        icon: convertCategoryToIcon(normalizedCategory),
        color:
            BillCategoryBackgroundColors[normalizedCategory] ??
            tokens.color.light.gray.gray300,
    };

    return mapPartialBillToBill(completedBill);
};

export const transformReviewBillFromServer = (bill: Required<_Bill>) => {
    const { amount: amountInCents, cadence, category } = bill;
    const amountInDollars = (amountInCents ?? 0) / 100;
    const transformed: IBill = {
        ...bill,
        amount: amountInDollars,
        monthlyAmount: getMonthlyBillAmount(amountInDollars, cadence),
        timingLabel: bill.timingLabel ?? getCadenceDisplayString(cadence),
        timingDescription:
            bill.timingDescription ?? getCadenceDisplayString(cadence),
        lastPaidAt: normalizeLastPaidAt(bill.lastPaidAt),
        icon: convertCategoryToIcon(category),
        color: BillCategoryBackgroundColors[category],
    };
    return transformed;
};

export const getBillTitle = (bill: PartialBill) => {
    const { displayName, category, payee, name } = bill;

    // Use displayName if it exists and is not empty
    if (displayName != null && displayName.trim() !== '') {
        return displayName;
    }

    // For certain categories, use the category name
    if (
        [BillCategory.CHILDCARE, BillCategory.RENT_MORTGAGE].includes(
            category as BillCategory,
        )
    ) {
        return BillCategoryNames[category as BillCategory];
    }

    // Use payee if it exists and is not empty
    if (payee != null && payee.trim() !== '') {
        return payee;
    }

    // Fallback to name if it exists and is not empty
    if (name != null && name.trim() !== '') {
        return name;
    }

    // Final fallback
    return 'Unknown Bill';
};

export const emptyBill = (): PartialBill => {
    return {
        amount: undefined,
        displayName: undefined,
        payee: undefined,
        category: undefined,
        monthlyAmount: undefined,
        cadence: undefined,
        timingLabel: undefined,
        timingDescription: undefined,
    };
};

export const emptyBillNew = (): ManualBill => {
    return {
        amount: undefined,
        displayName: undefined,
        payee: undefined,
        category: undefined,
        cadence: undefined,
        lastPaidAt: new Date(),
        accountId: undefined,
    };
};

export const getLastPaidDate = (day: string): Date => {
    const today = new Date();
    if (day) {
        const month = today.getMonth();
        const year = today.getFullYear();
        const dateThisMonth = new Date(year, month, Number(day));

        if (today.getDate() <= dateThisMonth.getDate()) {
            const previousMonth = month === 0 ? 11 : month - 1;
            const yearOfPreviousMonth = month === 0 ? year - 1 : year;
            const newLastPaidDate = new Date(
                yearOfPreviousMonth,
                previousMonth,
                Number(day),
            );
            return newLastPaidDate;
        }
        return dateThisMonth;
    }
    return today;
};

// sort by institution name ASC, then account name ASC
export const sortBillsAccounts = (
    billsAccounts: AccountSummary[],
): AccountSummary[] => {
    return billsAccounts.sort((a, b) => {
        return (
            a.institutionName.localeCompare(b.institutionName) ||
            a.name.localeCompare(b.name)
        );
    });
};

export const getTimingValuesForBill = (
    bill: PartialBill,
    lastPaidDate: Date,
): { timingLabel: string; timingDescription: string } => {
    const day = lastPaidDate?.getDate();
    const timingValues = { timingLabel: '', timingDescription: '' };

    if (bill.cadence === BillingCadence.MONTHLY) {
        timingValues.timingLabel = `${day}${getOrdinalSuffix(day)}`;
        timingValues.timingDescription = `Monthly around the ${timingValues.timingLabel}`;
    } else if (bill.cadence === BillingCadence.QUARTERLY) {
        timingValues.timingLabel = getCadenceDisplayString(bill.cadence);
        timingValues.timingDescription = 'Around the same time every quarter';
    } else if (bill.cadence === BillingCadence.ANNUALLY) {
        const monthStringLong = lastPaidDate.toLocaleDateString('default', {
            month: 'long',
        });
        const monthStringShort = lastPaidDate.toLocaleDateString('default', {
            month: 'short',
        });
        timingValues.timingLabel = monthStringShort;
        timingValues.timingDescription = `Every year in ${monthStringLong}`;
    } else if (bill.cadence === BillingCadence.SEMI_ANNUALLY) {
        timingValues.timingLabel = `${day}${getOrdinalSuffix(day)}`;
        timingValues.timingDescription = `Every six months around the ${timingValues.timingLabel}`;
    } else if (
        bill.cadence === BillingCadence.SEMI_MONTHLY ||
        bill.cadence === BillingCadence.BI_WEEKLY
    ) {
        timingValues.timingLabel = `${day}${getOrdinalSuffix(day)}`;
        timingValues.timingDescription = `Approximately every two weeks around the ${timingValues.timingLabel}`;
    } else if (bill.cadence === BillingCadence.WEEKLY) {
        const dayOfWeek = lastPaidDate?.getDay();
        timingValues.timingLabel = getDayOfWeekString(dayOfWeek);
        timingValues.timingDescription = `Weekly on ${timingValues.timingLabel}`;
    } else if (bill.cadence === BillingCadence.DAILY) {
        timingValues.timingLabel = getCadenceDisplayString(bill.cadence);
        timingValues.timingDescription = `Daily`;
    }

    return timingValues;
};

export const datePlusOffset = (date: Date | string) => {
    const convertedDate = convertToDate(date);
    // Get the local timezone offset in minutes and convert it to milliseconds
    const timezoneOffset = convertedDate.getTimezoneOffset() * 60000;
    return new Date(convertedDate.getTime() + timezoneOffset);
};
